import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Stack, Typography, styled } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import ModalsWrapper from 'components/general/dialogs/dialog-wrapper'
import { ControlledMuiTextField } from 'components/general/forms/rhf/controlled-mui-textfield'
import { QueryStatusView } from 'components/general/query-status.view'
import { useUpdateCompanyDetailsMutation } from 'redux/apis/companies-api'
import { Company, CompanyDetailsUpdateRequest } from '../models/company'
import { formatOrgNumber } from '../companies-helper'

const FORM_ID = 'edit-company-details-form'

type Props = {
    company: Company
    onClose: () => void
}

// cubit admin only: realtor offices change name/address, which they cannot edit themselves
export const EditCompanyDetailsDialog: React.FC<Props> = ({ company, onClose }) => {
    const { t } = useTranslation()
    const [update, { isLoading, isError, error }] = useUpdateCompanyDetailsMutation()

    const schema = useMemo(() => {
        const required = t('TEXT_REQUIRED', 'Påkrevd')
        return z.object({
            name: z.string().trim().min(1, { message: required }).max(200),
            address: z.object({
                addressText: z.string().trim().min(1, { message: required }).max(200),
                postalCode: z
                    .string()
                    .trim()
                    .regex(/^\d{4}$/, { message: t('TEXT_POSTAL_CODE_INVALID', 'Postnummer må være 4 siffer') }),
            }),
        })
    }, [t])

    const { handleSubmit, control } = useForm<CompanyDetailsUpdateRequest>({
        defaultValues: {
            name: company.name ?? '',
            address: {
                addressText: company.address?.addressText ?? '',
                postalCode: company.address?.postalCode ?? '',
            },
        },
        shouldFocusError: true,
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    })

    const onSubmit = async (values: CompanyDetailsUpdateRequest) => {
        try {
            await update({ id: company.id, update: values }).unwrap()
            onClose()
        } catch (err) {
            console.error(err)
        }
    }

    return (
        <ModalsWrapper
            open={true}
            title={t('TEXT_EDIT_COMPANY', 'Rediger meglerkontor')}
            handleClose={onClose}
            fullWidth={true}
            maxWidth="sm"
            body={
                <Form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
                    <Typography variant="body2" color="text.secondary">
                        {t('TEXT_ORG_NR', 'Org. nr.')}: {formatOrgNumber(company.organizationNr)}
                    </Typography>
                    <ControlledMuiTextField
                        control={control}
                        name="name"
                        label={t('TEXT_NAME', 'Navn')}
                        variant="outlined"
                        fullWidth
                    />
                    <ControlledMuiTextField
                        control={control}
                        name="address.addressText"
                        label={t('TEXT_ADDRESS', 'Adresse')}
                        variant="outlined"
                        fullWidth
                    />
                    <ControlledMuiTextField
                        control={control}
                        name="address.postalCode"
                        label={t('TEXT_POSTAL_CODE', 'Postnummer')}
                        variant="outlined"
                    />
                    <QueryStatusView error={error} isError={isError} />
                </Form>
            }
            actions={
                <ActionWrapper>
                    <Button onClick={onClose} disabled={isLoading}>
                        {t('TEXT_CANCEL', 'AVBRYT')}
                    </Button>
                    <LoadingButton
                        disableElevation
                        variant="contained"
                        color="accent"
                        type="submit"
                        form={FORM_ID}
                        loading={isLoading}
                    >
                        {t('TEXT_SAVE', 'LAGRE')}
                    </LoadingButton>
                </ActionWrapper>
            }
        />
    )
}

const Form = styled('form')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
}))

const ActionWrapper = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}))
